import { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import useToast from '../../../util/hooks/useToast';

import Input from '../../../components/Input';
import Button from '../../../components/Button';
import { encryptData } from '../../../util/helpers/encrypt';
import StrengthMeter from '../../../components/PasswordStrengthMeter';
import AuthHeader from '../../../components/AuthBackground/_partials/AuthHeader';
import {
  setForgotPassModal,
  setPreviousForgotPassModal
} from '../../../redux/slices/auth/forgotPassSlice';
import { setEmail } from '../../../redux/slices/user/userSlice';
import ArrowRight from '../../../assets/svg_components/ArrowRight';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { ReactComponent as PasswordInputIcon } from '../../../assets/images/password-input-icon.svg';
import { ReactComponent as RadioCheckSuccess } from '../../../assets/images/radio-check-success-icon.svg';
import { RESET_PASSWORD_BY_EMAIL, RESET_PASSWORD_BY_PHONE } from '../../../graphql/mutation';
import { ReactComponent as RadioCheckError } from '../../../assets/images/radio-check-fail-icon.svg';

import ForgotPassVStyles from './ForgotPasswordResetScreen.module.scss';

const ForgotPasswordResetModal = () => {
  const Toast = useToast();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const forgotPassSelector = useAppSelector((state) => state.forgotPassModal);
  const { phone, email } = useAppSelector((state) => state.user);
  const [resetPasswordByPhone] = useMutation(RESET_PASSWORD_BY_PHONE);
  const [resetPasswordByEmail] = useMutation(RESET_PASSWORD_BY_EMAIL);

  const [error, setError] = useState('');
  const [time, setTime] = useState(120);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);

  const [isEightError, setIsEightError] = useState<boolean>(true);
  const [isUpperError, setIsUpperError] = useState<boolean>(true);
  const [isLowerError, setIsLowerError] = useState<boolean>(true);
  const [isSpecialError, setIsSpecialError] = useState<boolean>(true);

  useEffect(() => {
    if (error) {
      Toast.error(error);
    }
  }, [error, setError]);

  useEffect(() => {
    let id: any;
    if (time > 0) {
      id = setInterval(() => {
        setTime(time - 1);
      }, 1000);
    }
    if (time === 0) {
      clearInterval(id);
    }
    return () => clearInterval(id);
  }, [time]);

  const passwordValidation = Yup.object().shape({
    password: Yup.string()
      .required('Required')
      .min(8, 'Must be at least 8 characters long')
      .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
      .matches(/([a-z])/, 'Must contain at least one lowercase letter')
      .matches(/(\W)/, 'Must contain at least one special character'),

    confirmPassword: Yup.string().oneOf([Yup.ref('password'), null], 'Passwords must match')
  });

  const resetByEmailFormik = useFormik({
    validationSchema: passwordValidation,
    initialValues: {
      password: '',
      confirmPassword: ''
    },
    onSubmit: async (values) => {
      try {
        const payload = {
          email,
          otp: forgotPassSelector?.resetOtp,
          newPassword: values.confirmPassword
        };
        setIsLoading(true);
        setError('');
        const reqPayload = await encryptData(JSON.stringify(payload));
        const resp = await resetPasswordByEmail({
          variables: {
            data: reqPayload
          }
        });
        if (resp?.data?.resetPasswordCa?.status === 200) {
          dispatch(setEmail(''));
          setIsLoading(false);
          setIsSuccess(true);
          setTime(120);
        }
        if (resp?.data?.resetPasswordCa?.status === 400) {
          setError(resp?.data?.resetPasswordCa?.message);
          setIsLoading(false);
        }
      } catch (err) {
        setIsLoading(false);
        const e = err as any;
        if (e && e.graphQLErrors && e.graphQLErrors.length > 0) {
          setError(e.graphQLErrors[0].message);
        } else {
          setError('Something went wrong');
        }
      }
    }
  });

  const resetByPhoneFormik = useFormik({
    validationSchema: passwordValidation,
    initialValues: {
      password: '',
      confirmPassword: ''
    },
    onSubmit: async (values) => {
      try {
        const payload = {
          phone,
          otp: forgotPassSelector?.resetOtp,
          newPassword: values.confirmPassword
        };
        setIsLoading(true);
        setError('');
        const reqPayload = await encryptData(JSON.stringify(payload));
        const resp = await resetPasswordByPhone({
          variables: {
            data: reqPayload
          }
        });
        if (resp?.data?.resetPasswordByPhoneCa?.status === 200) {
          dispatch(setEmail(''));
          setIsLoading(false);
          setIsSuccess(true);
        }
        if (resp?.data?.resetPasswordByPhoneCa?.status === 400) {
          setError(resp?.data?.resetPasswordByPhoneCa?.message);
          setIsLoading(false);
        }
      } catch (err) {
        setIsLoading(false);
        const e = err as any;
        if (e && e.graphQLErrors && e.graphQLErrors.length > 0) {
          setError(e.graphQLErrors[0].message);
        } else {
          setError('Something went wrong');
        }
      }
    }
  });

  useEffect(() => {
    passwordValidation
      .validate(
        {
          password:
            forgotPassSelector.userResetOption === 'EMAIL'
              ? resetByEmailFormik.values.password
              : resetByPhoneFormik.values.password
        },
        { abortEarly: false }
      )
      .then((valid) => {
        if (valid) {
          setIsEightError(false);
          setIsUpperError(false);
          setIsLowerError(false);
          setIsSpecialError(false);
        }
      })
      .catch((err) => {
        if (!err.errors.includes('Must contain at least one uppercase letter')) {
          setIsUpperError(false);
        } else {
          setIsUpperError(true);
        }

        if (!err.errors.includes('Must contain at least one lowercase letter')) {
          setIsLowerError(false);
        } else {
          setIsLowerError(true);
        }

        if (!err.errors.includes('Must contain at least one special character')) {
          setIsSpecialError(false);
        } else {
          setIsSpecialError(true);
        }

        if (!err.errors.includes('Must be at least 8 characters long')) {
          setIsEightError(false);
        } else {
          setIsEightError(true);
        }
      });
  }, [resetByEmailFormik.values.password, resetByPhoneFormik.values.password]);

  const onSuccess = () => {
    dispatch(setForgotPassModal(''));
    dispatch(setPreviousForgotPassModal(''));
    navigate('/');

    // To clear the current profile and its associated Redux store.
    window.location.reload();
  };

  return (
    <div className={ForgotPassVStyles.forgotPass}>
      <AuthHeader className={ForgotPassVStyles.forgotPass__authHeader} />
      {!isSuccess && (
        <div className={ForgotPassVStyles.forgotPass__content}>
          <h1 className={ForgotPassVStyles.forgotPass__headText}>Reset Your Password</h1>

          <p className={ForgotPassVStyles.forgotPass__subtext}>Set a new password below</p>

          <form
            className={ForgotPassVStyles.forgotPass__form}
            onSubmit={
              forgotPassSelector.userResetOption === 'EMAIL'
                ? resetByEmailFormik.handleSubmit
                : resetByPhoneFormik.handleSubmit
            }>
            {forgotPassSelector.userResetOption === 'EMAIL' ? (
              <div className={ForgotPassVStyles.forgotPass__passwordInput}>
                <Input
                  type="password"
                  name="password"
                  icon={<PasswordInputIcon />}
                  placeholder="Enter New Password"
                  value={resetByEmailFormik.values.password}
                  onChange={resetByEmailFormik.handleChange}
                  error={resetByEmailFormik.submitCount > 0 && resetByEmailFormik.errors.password}
                  onFocus={() => setIsPasswordFocused(true)}
                />
              </div>
            ) : (
              <div className={ForgotPassVStyles.forgotPass__passwordInput}>
                <Input
                  type="password"
                  name="password"
                  icon={<PasswordInputIcon />}
                  placeholder="Enter New Password"
                  value={resetByPhoneFormik.values.password}
                  onChange={resetByPhoneFormik.handleChange}
                  error={resetByPhoneFormik.submitCount > 0 && resetByPhoneFormik.errors.password}
                  onFocus={() => setIsPasswordFocused(true)}
                />
              </div>
            )}
            {isPasswordFocused && (
              <>
                <StrengthMeter
                  password={
                    resetByEmailFormik.values.password || resetByPhoneFormik.values.password
                  }
                />

                <div className={ForgotPassVStyles.forgotPass__validation}>
                  <div className={ForgotPassVStyles.forgotPass__validationText}>
                    <span className={ForgotPassVStyles.forgotPass__validationCriteria}>
                      {isEightError ? <RadioCheckError /> : <RadioCheckSuccess />} 8 character
                      minimum
                    </span>
                    <span className={ForgotPassVStyles.forgotPass__validationCriteria}>
                      {isLowerError ? <RadioCheckError /> : <RadioCheckSuccess />} a lowercase
                      letter
                    </span>
                    <span className={ForgotPassVStyles.forgotPass__validationCriteria}>
                      {isUpperError ? <RadioCheckError /> : <RadioCheckSuccess />} an uppercase
                      letter
                    </span>
                    <span className={ForgotPassVStyles.forgotPass__validationCriteria}>
                      {isSpecialError ? <RadioCheckError /> : <RadioCheckSuccess />} a symbol
                    </span>
                  </div>
                </div>
              </>
            )}

            {forgotPassSelector.userResetOption === 'EMAIL' ? (
              <div className={ForgotPassVStyles.forgotPass__confirmPasswordInput}>
                <Input
                  type="password"
                  name="confirmPassword"
                  icon={<PasswordInputIcon />}
                  placeholder="Confirm Password"
                  value={resetByEmailFormik.values.confirmPassword}
                  onChange={resetByEmailFormik.handleChange}
                  error={
                    resetByEmailFormik.submitCount > 0 && resetByEmailFormik.errors.confirmPassword
                  }
                  onFocus={() => setIsPasswordFocused(false)}
                />
              </div>
            ) : (
              <div className={ForgotPassVStyles.forgotPass__confirmPasswordInput}>
                <Input
                  type="password"
                  name="confirmPassword"
                  icon={<PasswordInputIcon />}
                  placeholder="Confirm Password"
                  value={resetByPhoneFormik.values.confirmPassword}
                  onChange={resetByPhoneFormik.handleChange}
                  error={
                    resetByPhoneFormik.submitCount > 0 && resetByPhoneFormik.errors.confirmPassword
                  }
                  onFocus={() => setIsPasswordFocused(false)}
                />
              </div>
            )}

            <Button
              className={ForgotPassVStyles.forgotPass__button}
              title="Update Password"
              type="submit"
              isLoading={isLoading}
              disabled={isLoading}
            />
            <div className={ForgotPassVStyles.forgotPass__redirectText}>
              <ArrowRight
                className={ForgotPassVStyles.forgotPass__redirectArrow}
                stroke="#5941A9"
              />
              <span
                className={ForgotPassVStyles.forgotPass__redirectClickable}
                onClick={() => {
                  navigate('/');
                }}>
                Go back to login
              </span>
            </div>
          </form>
        </div>
      )}

      {isSuccess && (
        <div className={ForgotPassVStyles.forgotPass__content}>
          <p className={ForgotPassVStyles.forgotPass__successHeadtext}>Password Reset Complete!</p>

          <p className={ForgotPassVStyles.forgotPass__successSubtext}>
            Your password was successfully reset. Login using your new password
          </p>

          <Button
            onPress={onSuccess}
            className={ForgotPassVStyles.forgotPass__successButton}
            title="Continue to Login"
          />
        </div>
      )}
    </div>
  );
};

export default ForgotPasswordResetModal;
